/**
 * Vera Rx route tree, mounted by the shell at /rx.
 *
 * ProtectedRoute requires an Rx.* role (Rx.User to read; global Admin passes).
 * Rx.Admin gates the three privileged acts server-side (refresh, expectations,
 * prospect uploads); the UI hides what it can, but the BFF is the enforcer.
 *
 * The module has two halves and the nav says so: what our book of business has
 * actually done, then what a prospect might do based on it.
 */

import { lazy, Suspense } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import {
  DataHistogramRegular,
  TargetArrowRegular,
  BranchForkRegular,
  LibraryRegular,
  MoneyHandRegular,
  DatabaseRegular,
  PillRegular,
  BuildingBankRegular,
  PeopleTeamRegular,
} from '@fluentui/react-icons';
import { AppShell, type NavEntry } from '@widgets/app-shell';
import { ErrorBoundary } from '@shared/ui';
import { ProtectedRoute } from '@shared/auth';

/** Clarity Green: the module's locked accent, per the brand guide. */
export const RX_ACCENT = '#597B4B';

const RxAnalyticsPage = lazy(() =>
  import('@rx/pages/analytics').then((m) => ({ default: m.RxAnalyticsPage }))
);
const RxSavingsPage = lazy(() =>
  import('@rx/pages/savings').then((m) => ({ default: m.RxSavingsPage }))
);
const RxBrandMixPage = lazy(() =>
  import('@rx/pages/brand-mix').then((m) => ({ default: m.RxBrandMixPage }))
);
const RxWellstarPage = lazy(() =>
  import('@rx/pages/wellstar').then((m) => ({ default: m.RxWellstarPage }))
);
const RxProspectsPage = lazy(() =>
  import('@rx/pages/prospects').then((m) => ({ default: m.RxProspectsPage }))
);
const RxExpectationsPage = lazy(() =>
  import('@rx/pages/expectations').then((m) => ({ default: m.RxExpectationsPage }))
);
const RxMappingPage = lazy(() =>
  import('@rx/pages/mapping').then((m) => ({ default: m.RxMappingPage }))
);
const RxReferencesPage = lazy(() =>
  import('@rx/pages/references').then((m) => ({ default: m.RxReferencesPage }))
);
const RxEvidencePage = lazy(() =>
  import('@rx/pages/evidence').then((m) => ({ default: m.RxEvidencePage }))
);

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>{children}</Suspense>;
}

function RxLayout({ basePath }: { basePath: string }) {
  const nav: NavEntry[] = [
    { path: `${basePath}/savings-experience`, label: 'Savings experience', icon: MoneyHandRegular },
    { path: `${basePath}/book-of-business`, label: 'Book of business', icon: DataHistogramRegular },
    { path: `${basePath}/brand-generic`, label: 'Brand and generic', icon: PillRegular },
    { path: `${basePath}/prospects`, label: 'Prospect analyses', icon: PeopleTeamRegular },
    { path: `${basePath}/expectations`, label: 'Savings expectations', icon: TargetArrowRegular },
    { path: `${basePath}/mapping`, label: 'Channel mapping', icon: BranchForkRegular },
    { path: `${basePath}/lists`, label: 'Reference lists', icon: LibraryRegular },
    { path: `${basePath}/evidence`, label: 'Evidence', icon: DatabaseRegular },
    // One named prospect with a route of its own, because Wellstar was needed
    // before the general workbench was (Sasha, 2026-08-17). Under its own
    // heading at the bottom so the shortcut reads as a shortcut: the permanent
    // home is Prospect analyses above.
    { kind: 'section', label: 'Temporary' },
    { path: `${basePath}/wellstar`, label: 'Wellstar', icon: BuildingBankRegular },
  ];

  return (
    <ProtectedRoute appRolePrefix="Rx">
      <AppShell appId="rx" appTitle="Rx Savings Analysis" navItems={nav} headerColor={RX_ACCENT}>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </AppShell>
    </ProtectedRoute>
  );
}

export function rxRoutes(basePath: string): RouteObject[] {
  return [
    {
      element: <RxLayout basePath={basePath} />,
      children: [
        { index: true, element: <Navigate to="savings-experience" replace /> },
        {
          path: 'savings-experience',
          element: (
            <LazyPage>
              <RxSavingsPage />
            </LazyPage>
          ),
        },
        {
          path: 'book-of-business',
          element: (
            <LazyPage>
              <RxAnalyticsPage />
            </LazyPage>
          ),
        },
        {
          path: 'brand-generic',
          element: (
            <LazyPage>
              <RxBrandMixPage />
            </LazyPage>
          ),
        },
        {
          path: 'wellstar',
          element: (
            <LazyPage>
              <RxWellstarPage />
            </LazyPage>
          ),
        },
        {
          path: 'prospects',
          element: (
            <LazyPage>
              <RxProspectsPage />
            </LazyPage>
          ),
        },
        {
          path: 'expectations',
          element: (
            <LazyPage>
              <RxExpectationsPage />
            </LazyPage>
          ),
        },
        {
          path: 'mapping',
          element: (
            <LazyPage>
              <RxMappingPage />
            </LazyPage>
          ),
        },
        {
          path: 'lists',
          element: (
            <LazyPage>
              <RxReferencesPage />
            </LazyPage>
          ),
        },
        {
          path: 'evidence',
          element: (
            <LazyPage>
              <RxEvidencePage />
            </LazyPage>
          ),
        },
      ],
    },
  ];
}
